@file:OptIn(ExperimentalJsExport::class)

package com.siriusxm.cmc

import kotlin.js.Promise

/**
 * Provides an API for embedded iFrame applications to access global
 * CMC functionality.
 */
@OptIn(ExperimentalJsExport::class)
@JsExport
public interface CMCContainer {
    /**
     * Navigate to an origin relative location (after the #)
     */
    public fun navigate(path: String)

    /**
     * Navigate to an origin-relative location and label the resulting history
     * entry with [title]. Browsers ignore pushState's title argument, so the
     * label is applied via document.title once the navigation lands. Use this
     * when the iframe app knows a meaningful page title (entity name, search
     * query, etc.) — back/forward dropdowns then show that label instead of
     * the generic app name.
     */
    public fun navigateWithTitle(path: String, title: String) {
        navigate(path)
    }

    /**
     * The current user of the CMC application.
     */
    public val viewer: CMCViewer

    /**
     * The audio player interface
     */
    public val audioPlayer: CMCAudioPlayer

    /**
     * Get a global configuration property by key.
     */
    public fun configProperty(key: String): String?

    /**
     * The IANA id of the user-selected time zone (e.g. "America/New_York"), chosen via the global
     * timezone selector in the CMC header. Embedded apps should render times in this zone so they
     * stay consistent with the rest of the console. The parent hard-reloads the page (and thus this
     * iframe) whenever the selection changes, so this value is stable for the lifetime of a load;
     * the same value is also delivered as a `timezone` query parameter on the iframe URL.
     */
    public val timeZoneId: String

    /**
     * Access the Notifications system.
     */
    public val notifications: Notifications
}

/**
 * Get te stage (dev/test/prod) that this container is running in.
 */
public val CMCContainer.stage: String get() = configProperty("stage") ?: "dev"

/**
 * The current CMC user.
 */
@JsExport
public interface CMCViewer {
    /**
     * The user id
     */
    public val id: String

    /**
     * The access token for API access.
     */
    public val accessToken: String

    /**
     * The user email
     */
    public val email: String

    /**
     * The list of groups this user belongs to.
     */
    public val groups: Array<String>

    /**
     * The username extracted from the email (part before @).
     * Defaults to lowercase email prefix.
     */
    public val username: String
        get() = email.substringBefore('@').lowercase()

    /**
     * Refresh token (if necessary). Returns the new accessToken.
     */
    public fun refresh(): Promise<String>
}